'use client';

/**
 * EditorialOfferingHero — the structural editorial offering hero rendered for
 * the `se3` Builder variant (PD — se3 editorial offering).
 *
 * This is the "two versions" approach: when the active Builder variant is
 * `se3` (`isEditorialReskin`), `BuilderLayoutClient` renders THIS React
 * component above the Builder-authored body and hides the default Builder hero
 * block (`[data-cy="builder-hero-section"]`) via a scoped CSS rule. For every
 * other variant (including `control`) this component never mounts, so the
 * control path is byte-for-byte unchanged.
 *
 * Everything here is data-driven from `offeringData`. The bespoke marketing
 * pieces from the design handoff that do NOT exist in `offeringData` (the
 * italic serif tagline line, the partner-logo card, image-overlay copy and
 * footnotes) are exposed as OPTIONAL slots: they render only when explicitly
 * provided (`tagline`, `heroImage`, `partnerCard`), otherwise they are omitted
 * gracefully. These are the remaining Builder-content authoring TODOs.
 *
 * Built from the se3 design-system tokens (`@startengine/design-system.se3.tokens`)
 * and the HomepageV3 editorial wrappers (`Badge`, `EditorialButton`) so the
 * typography / spacing / color match HomepageV3 and the handoff.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import { useSelector } from 'react-redux';

import { se3Colors, se3FontFamily } from '@startengine/design-system.se3.tokens';
import { colors } from '@startengine/design-system.theme.main';
// Handoff accent gold (#B58A4B) — keeps the hero consistent with the offering
// body sections; se3Colors.gold (#8C6A2C) is a darker token that drifts.
import { ACCENT_GOLD, TEAL } from './editorialSection';
import { Badge } from 'components/HomepageV3/components/Badge';
import { HERO_COPY_MAX_WIDTH } from '../heroLayout';
import EditorialButton from 'components/HomepageV3/components/EditorialButton';
import { formatCurrency, getEnv, isRetirementFundsOnly } from 'utils/helpers';
import {
  isAmountBasedSecurity,
  inferRegulationTypeFromSlug,
  mapRegulationTypeToCanonical,
  resolveOfferingInvestCtaText,
  resolveCurrentAmountRaised,
  isOverAmountRaised,
} from 'utils/offerings/helpers';
import useLoginModal from 'hooks/useLoginModal';
import useExcludeAmountOfferingList from 'hooks/useExcludeAmountOfferingList';
import useRegDExcludedList from 'hooks/useRegDExcludedList';
import useShowAmountRaisedList from 'hooks/useShowAmountRaisedList';
import useOfferingDaysLeft from 'hooks/useOfferingDaysLeft';
import {
  ADD_TO_CART_SURFACES,
  appendPostHogDistinctId,
  investNowClick,
  trackEvent,
  trackSEEvent,
} from 'utils/analytics';
import { INVESTOR_SIGNALS_BEHAVIORAL_EVENTS } from 'utils/investorSignalsBehavioralProperties';
import OfferingLegalText from 'components/Offering/OfferingLegalText';
import { isTtwOffering } from 'components/Offering/BuilderLayout/components/shared/offeringStatus';

const REGULATION_LABELS = {
  crowdfunding: 'REG CF',
  reg_a: 'REG A+',
  private: 'REG D',
};

const REGULATION_EYEBROW = {
  crowdfunding: 'REGULATION CROWDFUNDING',
  reg_a: 'REGULATION A+',
  private: 'REGULATION D',
};

const WAITLIST_TOOLTIP =
  'This campaign has exceeded its maximum funding goal. You can still invest, but you will be added to a waitlist.';
const MIN_AMOUNT_RAISED_TO_DISPLAY = 124000;

const parseAmount = (value) => {
  if (value == null) return 0;
  const num = typeof value === 'string' ? Number.parseFloat(value) : value;
  return Number.isFinite(num) ? num : 0;
};

const normalizeType = (value) =>
  value == null
    ? ''
    : String(value)
        .trim()
        .toLowerCase()
        .replace(/[\s_\-.()]/g, '');

const resolveSecurityPillLabel = (offeringData, canonicalRegulation) => {
  if (isAmountBasedSecurity(offeringData)) {
    const type = normalizeType(
      offeringData?.offeringType ||
        offeringData?.securityType ||
        offeringData?.securityName ||
        offeringData?.assetType,
    );
    if (type === 'safe') return 'SAFE';
    if (type === 'debt') return 'DEBT';
    return 'CONVERTIBLE NOTE';
  }

  return canonicalRegulation === 'private' ? 'PRIVATE' : 'COMMON STOCK';
};

const normalizeText = (value) => {
  if (typeof value !== 'string') return '';
  return value.replace(/\s+/g, ' ').trim();
};

const firstText = (...candidates) => {
  for (const candidate of candidates) {
    const text = normalizeText(candidate);
    if (text) return text;
  }
  return '';
};

const sameText = (a, b) => normalizeText(a).toLowerCase() === normalizeText(b).toLowerCase();

const BRAND_NAME_OVERRIDES = {
  sambanova: 'SambaNova',
};

const applyKnownBrandCase = (value) => {
  const text = normalizeText(value);
  if (!text) return '';

  const key = text.toLowerCase().replace(/[^a-z0-9]/g, '');
  return BRAND_NAME_OVERRIDES[key] || text;
};

const resolveCompanyNameFromSeriesName = (value) => {
  const text = normalizeText(value);
  if (!/^series\s+/i.test(text)) return '';

  return applyKnownBrandCase(text.replace(/^series\s+/i, '').replace(/-?qp-\d+$/i, ''));
};

export const resolvePortfolioCompanyName = (offeringData) =>
  firstText(
    offeringData?.portfolioCompanyName,
    offeringData?.portfolio_company_name,
    offeringData?.underlyingCompanyName,
    offeringData?.underlying_company_name,
    offeringData?.companyName,
    offeringData?.company_name,
    offeringData?.company?.dbaName,
    offeringData?.company?.name,
    resolveCompanyNameFromSeriesName(offeringData?.name),
    offeringData?.name,
  );

export const resolveSeriesLegalName = (offeringData, portfolioCompany) => {
  const explicitSeries = firstText(
    offeringData?.series,
    offeringData?.seriesName,
    offeringData?.series_name,
    offeringData?.entityName,
    offeringData?.entity_name,
    offeringData?.entity?.name,
    offeringData?.entity?.legalName,
  );
  if (explicitSeries && !sameText(explicitSeries, portfolioCompany)) return explicitSeries;

  const backendName = normalizeText(offeringData?.name);
  if (backendName && !sameText(backendName, portfolioCompany)) return backendName;

  const legalEntity = firstText(offeringData?.issuerName, offeringData?.company?.legalName);
  if (legalEntity && !sameText(legalEntity, portfolioCompany)) return legalEntity;

  return '';
};

const isQualifiedPurchaserOnly = (offeringData) =>
  normalizeText(offeringData?.targetAudienceAccreditation).toUpperCase() === 'QP';

// A few legacy/private offering payloads omit `investmentSourceOfFunds` even
// though the campaign route is explicitly an IRA offering (for example
// `/offering/openai-ira`). Treat the route name as a safe fallback so the
// visible IRA status cannot disappear while the API payload is incomplete.
const isIraOffering = (offeringData) =>
  isRetirementFundsOnly(offeringData) || /(?:^|-)ira(?:-|$)/i.test(offeringData?.slug || '');

// The legacy OpenAI IRA payload is also missing its canonical waitlist field.
// Keep that one established exception until the API supplies the field, but do
// not infer waitlist status from every IRA route. Waitlist is an offering
// status, not an IRA eligibility attribute.
const LEGACY_WAITLISTED_SLUGS = new Set(['openai-ira']);

const isLegacyWaitlistedOffering = (offeringData) =>
  LEGACY_WAITLISTED_SLUGS.has(String(offeringData?.slug || '').toLowerCase());

/**
 * Builds the editorial regulation pill label + eyebrow regulation suffix from
 * whatever raw `regulationType` the offerings API returns ("Reg CF", "506(c)",
 * "Regulation A+", canonical forms, etc.), normalizing via the shared
 * canonical mapper so every variant collapses to a stable label.
 */
const resolveRegulation = (offeringData) => {
  const canonical =
    mapRegulationTypeToCanonical(offeringData?.regulationType) ||
    inferRegulationTypeFromSlug(offeringData?.slug);
  return {
    canonical,
    pill: canonical
      ? `${REGULATION_LABELS[canonical]} · ${resolveSecurityPillLabel(offeringData, canonical)}`
      : null,
    eyebrow: canonical ? REGULATION_EYEBROW[canonical] : null,
  };
};

export const buildInvestUrl = (slug, isReserved = false) => {
  if (!slug) return '#';
  return `${getEnv()}investment.startengine.com/${slug}${isReserved ? '/reserve' : ''}`;
};

const buildVimeoEmbedUrl = (videoUrl, background) => {
  const value = typeof videoUrl === 'string' ? videoUrl.trim() : '';
  const idMatch = value.match(/^(\d+)(?:\?|$)/) || value.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (!idMatch?.[1]) return null;

  const hashMatch = value.match(/[?&]h=([a-zA-Z0-9]+)/);
  const params = new URLSearchParams(
    background ? { background: '1', playsinline: '1' } : { autoplay: '1' },
  );
  if (hashMatch?.[1]) params.set('h', hashMatch[1]);

  return `https://player.vimeo.com/video/${idMatch[1]}?${params.toString()}`;
};

const EditorialOfferingHero = ({
  offeringData,
  tagline = null,
  eyebrowCategory = null,
  heroImage: authoredHeroImage = null,
  partnerCard = null,
  showMomentumPill = false,
  amountRaisedData = null,
  backgroundVideoUrl = null,
}) => {
  const offeringCoverImage = offeringData?.coverDesktopUrl || offeringData?.coverTombstoneUrl;
  const heroImage = authoredHeroImage || (offeringCoverImage ? { src: offeringCoverImage } : null);
  const heroImageObjectFit =
    authoredHeroImage?.objectFit || (authoredHeroImage ? 'contain' : 'cover');
  const heroImageScale = offeringData?.slug === 'greenfield-robotics' ? 1.17 : 1;
  const name = resolvePortfolioCompanyName(offeringData) || 'this company';
  const seriesLegalName = resolveSeriesLegalName(offeringData, name);
  // PD (Paige QA): the headline should be the marketing title language (e.g.
  // "Peter Thiel's Bet on AI Data Centers…"), not the series/company name.
  // Prefer a marketing-headline field; fall back to the name so we never render
  // the (long) elevator pitch here. The subhead "Series <name>" is dropped —
  // the series already appears in the eyebrow.
  // `content` is the marketing headline the control hero renders as its tagline
  // (Heading.jsx `heroContent`); the longer paragraph is `elevatorPitch` (used
  // as `description` below). Fall back to the series/company name when empty.
  const heroTitle =
    firstText(
      offeringData?.content,
      offeringData?.heroTitle,
      offeringData?.headline,
      offeringData?.tagline,
      offeringData?.pitchTitle,
    ) || name;
  // Design headline splits into a lead line + an italic-teal tagline (e.g.
  // "Invest in Atombeam." / "Partnering with NVIDIA…"). The onboarding `content`
  // field carries that as two newline-separated lines; `heroTitle` above
  // collapses the whitespace, so re-derive the split from the raw content. When
  // there's no 2nd line we render a single headline (no forced teal split).
  const rawContent = typeof offeringData?.content === 'string' ? offeringData.content : '';
  const contentLines = rawContent
    .split(/\r?\n+/)
    .map((line) => normalizeText(line))
    .filter(Boolean);
  const headlineLead = contentLines.length > 1 ? contentLines[0] : heroTitle;
  const headlineTagline = contentLines.length > 1 ? contentLines.slice(1).join(' ') : null;
  const slug = offeringData?.slug;
  const user = useSelector((state) => state.user);
  const {
    needToLogin,
    verifiedPrimaryUserId,
    loading: partnerLoading,
  } = useSelector((state) => state.partnerVerification);
  const [renderLoginModal, setLoginModalOpen] = useLoginModal();
  const [redirectUrl, setRedirectUrl] = useState('');
  const [pitchExpanded, setPitchExpanded] = useState(false);
  const [pitchCanExpand, setPitchCanExpand] = useState(false);
  const pitchRef = useRef(null);
  // Preserve the published Builder hero's autoplay/background setting when
  // SE3 replaces that block. Other offering videos remain click-to-play.
  const [videoOpen, setVideoOpen] = useState(false);
  const [backgroundVideoUnmuted, setBackgroundVideoUnmuted] = useState(false);
  const [backgroundVideoPlaying, setBackgroundVideoPlaying] = useState(true);
  const backgroundVideoIframeRef = useRef(null);
  const videoUrl = backgroundVideoUrl || offeringData?.videoUrl;
  const autoplayBackgroundVideo = Boolean(backgroundVideoUrl);
  const isVideoOpen = videoOpen || autoplayBackgroundVideo;
  const videoEmbedSrc = useMemo(() => {
    if (!videoUrl) return null;
    if (videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be')) {
      const m = videoUrl.match(/(?:youtu\.be\/|[?&]v=|\/embed\/)([^&\n?/]{11})/);
      return m ? `https://www.youtube.com/embed/${m[1]}?autoplay=1` : null;
    }
    if (/^\d+(?:\?|$)/.test(videoUrl.trim()) || videoUrl.includes('vimeo.com')) {
      return buildVimeoEmbedUrl(videoUrl, autoplayBackgroundVideo);
    }
    return null;
  }, [autoplayBackgroundVideo, videoUrl]);

  const sendBackgroundVideoCommand = useCallback((method, value) => {
    const playerWindow = backgroundVideoIframeRef.current?.contentWindow;
    if (!playerWindow) return;

    const message = { method };
    if (value !== undefined) message.value = value;
    playerWindow.postMessage(JSON.stringify(message), 'https://player.vimeo.com');
  }, []);

  const handleBackgroundVideoUnmute = useCallback(() => {
    setBackgroundVideoUnmuted(true);
    setBackgroundVideoPlaying(true);
    sendBackgroundVideoCommand('setMuted', false);
    sendBackgroundVideoCommand('setCurrentTime', 0);
    sendBackgroundVideoCommand('play');
    trackEvent('builder_hero_video_unmute', { offering_slug: slug });
  }, [sendBackgroundVideoCommand, slug]);

  const handleBackgroundVideoPlayPause = useCallback(() => {
    const method = backgroundVideoPlaying ? 'pause' : 'play';
    sendBackgroundVideoCommand(method);
    setBackgroundVideoPlaying((playing) => !playing);
    trackEvent(`builder_hero_video_${method}`, { offering_slug: slug });
  }, [backgroundVideoPlaying, sendBackgroundVideoCommand, slug]);

  let backgroundVideoControlLabel = 'Unmute and play from start';
  let backgroundVideoControlIcon = <VolumeOffIcon sx={{ fontSize: 32 }} />;
  if (backgroundVideoUnmuted) {
    backgroundVideoControlLabel = backgroundVideoPlaying ? 'Pause video' : 'Play video';
    backgroundVideoControlIcon = backgroundVideoPlaying ? (
      <PauseIcon sx={{ fontSize: 24 }} />
    ) : (
      <PlayArrowIcon sx={{ fontSize: 24 }} />
    );
  }

  const regulation = useMemo(() => resolveRegulation(offeringData), [offeringData]);
  const isTTW = isTtwOffering(offeringData);
  const isPrivateOffering = regulation.canonical === 'private';
  const showQpBadge = isQualifiedPurchaserOnly(offeringData);
  const [getTillOfferingClose] = useOfferingDaysLeft();
  const daysLeft = getTillOfferingClose(offeringData);
  // A waitlisted offering is still open for interest, whereas a closed
  // offering is terminal. Keep that distinction available to both the status
  // pills and CTA below so the two surfaces cannot drift.
  const isClosed = offeringData?.status === 'closed';
  const isWaitlisted =
    !isClosed &&
    (isOverAmountRaised(offeringData) ||
      Boolean(offeringData?.lockedOnWaitlist) ||
      // OpenAI IRA is the one legacy campaign whose payload omits the canonical
      // waitlist field. Other IRA/Reg D offerings must use the API status above.
      (isPrivateOffering && isLegacyWaitlistedOffering(offeringData)));

  // Status and eligibility are both meaningful at a glance. In particular,
  // waitlisted offerings such as OpenAI-IRA need their waitlist state visible
  // before the CTA, matching the campaign-page QA target.
  const heroBadges = useMemo(() => {
    const badges = [];
    if (isTTW) {
      badges.push({ key: 'ttw', label: 'Testing the Waters', variant: 'wine' });
    }
    if (isClosed) {
      badges.push({ key: 'closed', label: 'Offering Closed', variant: 'wine' });
    }
    if (isWaitlisted) {
      badges.push({
        key: 'waitlist',
        label: 'Waitlist',
        variant: 'gold',
        tooltip: WAITLIST_TOOLTIP,
      });
    }
    if (daysLeft && !isClosed) {
      badges.push({ key: 'days-left', label: daysLeft, variant: 'wine' });
    }
    if (showQpBadge) {
      badges.push({ key: 'qp', label: 'Qualified Purchasers Only', variant: 'teal' });
    } else if (isPrivateOffering) {
      badges.push({ key: 'accredited', label: 'Accredited Investors Only', variant: 'teal' });
    }
    if (isIraOffering(offeringData)) {
      badges.push({ key: 'ira', label: 'Tax-Advantaged (IRA)', variant: 'gold' });
    }
    if (offeringData?.internationalInvestorsAllowed === false) {
      badges.push({ key: 'us', label: 'US Investors Only', variant: 'teal' });
    }
    return badges;
  }, [daysLeft, isClosed, isPrivateOffering, isTTW, isWaitlisted, offeringData, showQpBadge]);

  const eyebrow = useMemo(() => {
    // Private/Reg D leads with the configured onboarding name. This preserves
    // legal series names when they are explicitly configured while avoiding a
    // synthetic "Series <slug>" label for names such as "Spark Fund 2".
    if (isPrivateOffering) {
      const offeringName = firstText(
        offeringData?.name,
        offeringData?.series,
        offeringData?.seriesName,
        offeringData?.series_name,
        slug,
      );
      if (offeringName) return `INVEST IN ${offeringName.toUpperCase()}`;
    }
    // Design eyebrow is "CATEGORY · REGULATION" (e.g. "Data Compaction ·
    // Regulation A+"). When no explicit eyebrowCategory prop is passed, derive
    // the category from the offering's taxonomy (categories[0].name) so the
    // eyebrow matches the design structure instead of showing the regulation
    // alone. (Paige QA, 2026-07-09.)
    const derivedCategory = firstText(
      eyebrowCategory,
      offeringData?.categories?.[0]?.name,
      offeringData?.category,
      offeringData?.industry,
    );
    const parts = [];
    if (derivedCategory) parts.push(derivedCategory.toUpperCase());
    if (regulation.eyebrow) parts.push(regulation.eyebrow);
    return parts.join(' · ');
  }, [
    isPrivateOffering,
    offeringData?.name,
    offeringData?.series,
    offeringData?.seriesName,
    offeringData?.series_name,
    offeringData?.categories,
    offeringData?.category,
    offeringData?.industry,
    slug,
    eyebrowCategory,
    regulation.eyebrow,
  ]);

  const description = offeringData?.elevatorPitch || offeringData?.summary || null;

  useEffect(() => {
    const pitchElement = pitchRef.current;
    if (!pitchElement || pitchExpanded) return undefined;

    const updatePitchOverflow = () => {
      setPitchCanExpand(pitchElement.scrollHeight > pitchElement.clientHeight + 1);
    };

    updatePitchOverflow();

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(updatePitchOverflow);
      observer.observe(pitchElement);
      return () => observer.disconnect();
    }

    window.addEventListener('resize', updatePitchOverflow);
    return () => window.removeEventListener('resize', updatePitchOverflow);
  }, [description, pitchExpanded]);

  const amountRaised = parseAmount(
    resolveCurrentAmountRaised({ amountRaisedObject: amountRaisedData, offeringData }),
  );
  // Match the control hero: keep the amount hidden until the Strapi visibility
  // lists load, require the $124K threshold, apply the Reg D rule, and honor
  // explicit hide/show overrides.
  const { data: excludeAmountSlugs, isSuccess: loadedExcludeAmountSlugs } =
    useExcludeAmountOfferingList();
  const { data: regDExcludedSlugs, isSuccess: loadedRegDExcludedSlugs } = useRegDExcludedList();
  const { data: showAmountSlugs, isSuccess: loadedShowAmountSlugs } = useShowAmountRaisedList();
  const isAmountExcluded = Array.isArray(excludeAmountSlugs) && excludeAmountSlugs.includes(slug);
  const isAmountExplicitlyShown = Array.isArray(showAmountSlugs) && showAmountSlugs.includes(slug);
  const isRegDVisibilityException =
    Array.isArray(regDExcludedSlugs) && regDExcludedSlugs.includes(slug);
  const isRegD = offeringData?.regulationType === 'Reg D';
  const isRule506C = offeringData?.regulationType === '506(c)';
  const isPrivateAmountHidden = (isRegD && !isRegDVisibilityException) || isRule506C;
  const amountVisibilitySettingsLoaded =
    loadedExcludeAmountSlugs && loadedRegDExcludedSlugs && loadedShowAmountSlugs;
  const showRaised =
    !isAmountExcluded &&
    (isAmountExplicitlyShown ||
      (amountVisibilitySettingsLoaded &&
        !isPrivateAmountHidden &&
        amountRaised >= MIN_AMOUNT_RAISED_TO_DISPLAY));

  // PD (Paige QA, 2026-07-09): the editorial hero does NOT carry a min/PPS stat
  // row — neither design mock (AtomBeam Reg A, SambaNova QP) shows one in the
  // hero; those figures live in the invest sidebar. Removed to match the design.

  const investUrl = buildInvestUrl(slug, isTTW);
  // A closed offering must read "Offering Closed" on the hero CTA to match the
  // hero's own closed badge and the invest sidebar — not "Join the Waitlist" /
  // "Get Equity" (QA: closed offering still showed Join Waitlist on the hero).
  const ctaText = isTTW
    ? 'RESERVE NOW'
    : resolveOfferingInvestCtaText({
        offeringData,
        configuredText: 'GET EQUITY',
      }).toUpperCase();
  // Use the same reg-appropriate legal text control renders (Heading.jsx) for
  // ALL offerings — Reg D, TTW, and live Reg CF/A+ — rather than a hardcoded
  // TTW "solicitation of an indication of interest" line, which was wrong for
  // live crowdfunding offerings like CancerVAX (Paige QA 2026-07-09).
  const hasHeroImageOverlayContent = Boolean(heroImage?.headline || heroImage?.footnote);
  const navigateToInvestUrl = useCallback((targetUrl) => {
    if (!targetUrl || targetUrl === '#' || typeof window === 'undefined') return;
    const search = window.location.search || '';
    window.location.href = appendPostHogDistinctId(`${targetUrl}${search}`);
  }, []);
  // Get-Equity click analytics, mirroring the invest sidebar
  // (BuilderClassicInvestSidebar.trackInvestClickAnalytics). Fire-and-forget:
  // the trackers swallow their own errors, so awaiting them would only delay
  // navigation.
  const trackInvestClickAnalytics = useCallback(() => {
    if (!offeringData) return;
    investNowClick({ offeringData, user, surface: ADD_TO_CART_SURFACES.CAMPAIGN_PAGE });
    trackSEEvent(INVESTOR_SIGNALS_BEHAVIORAL_EVENTS.clickInvestNow, slug, user);
    trackEvent(INVESTOR_SIGNALS_BEHAVIORAL_EVENTS.clickInvestNow, offeringData);
  }, [offeringData, slug, user]);

  const handleCtaClick = useCallback(
    (event) => {
      // Must run before the signed-in early return below: signed-in users
      // navigate straight through the anchor's href, so bailing out first
      // dropped their `investNowClick` / add-to-cart events entirely.
      trackInvestClickAnalytics();

      if (user?.signedIn && !(needToLogin && !partnerLoading)) return;
      if (!investUrl || investUrl === '#') return;

      event?.preventDefault?.();
      setRedirectUrl(investUrl);
      setLoginModalOpen(true);
    },
    [
      investUrl,
      needToLogin,
      partnerLoading,
      setLoginModalOpen,
      trackInvestClickAnalytics,
      user?.signedIn,
    ],
  );

  return (
    <Box
      component="section"
      data-cy="editorial-offering-hero"
      sx={{
        backgroundColor: se3Colors.cream,
        color: se3Colors.ink,
        fontFamily: se3FontFamily.sans,
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      <Box
        sx={{
          maxWidth: '1240px',
          margin: '0 auto',
          padding: '40px 24px 56px',
          display: 'grid',
          gridTemplateColumns: '1fr',
          gap: '40px',
          alignItems: 'start',
          '@media (min-width: 1024px)': {
            // Design: fixed 480px right column so the hero image never shrinks
            // as the viewport narrows (Hannah QA). Left column takes the rest.
            gridTemplateColumns: 'minmax(0, 1fr) 480px',
            gap: '72px',
            padding: '56px 40px 64px',
          },
        }}
      >
        {/* Left column */}
        <Box sx={{ minWidth: 0 }}>
          <Box
            data-cy="editorial-offering-hero-pills"
            sx={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '24px' }}
          >
            {showMomentumPill && (
              <Badge variant="gold" dot data-cy="editorial-offering-hero-momentum-pill">
                MOST MOMENTUM
              </Badge>
            )}
            {heroBadges.map((badge) => (
              <Badge
                key={badge.key}
                variant={badge.variant}
                dot={badge.dot}
                data-cy={`editorial-offering-hero-badge-${badge.key}`}
              >
                {badge.label}
                {badge.tooltip && (
                  <Tooltip title={badge.tooltip} arrow>
                    <Box
                      component="span"
                      aria-label={`${badge.label} information`}
                      sx={{ display: 'inline-flex', marginLeft: '5px', verticalAlign: 'middle' }}
                    >
                      <InfoOutlinedIcon sx={{ fontSize: '14px' }} />
                    </Box>
                  </Tooltip>
                )}
              </Badge>
            ))}
          </Box>

          {eyebrow && (
            <Box
              component="p"
              data-cy="editorial-offering-hero-eyebrow"
              sx={{
                margin: '0 0 18px',
                fontFamily: se3FontFamily.sans,
                fontSize: '12px',
                fontWeight: 600,
                letterSpacing: '0.28em',
                textTransform: 'uppercase',
                color: ACCENT_GOLD,
              }}
            >
              {eyebrow}
            </Box>
          )}

          <Box
            component="h1"
            data-cy="editorial-offering-hero-headline"
            sx={{
              fontFamily: se3FontFamily.serif,
              fontWeight: 400,
              // PD (Paige QA): design headline is 46px on desktop (was 58px).
              fontSize: '34px',
              lineHeight: 1.06,
              letterSpacing: '-0.015em',
              color: se3Colors.ink,
              margin: 0,
              textWrap: 'pretty',
              '@media (min-width: 768px)': { fontSize: '40px' },
              '@media (min-width: 1024px)': { fontSize: '46px' },
            }}
          >
            {headlineLead}
            {(tagline || headlineTagline) && (
              <Box
                component="span"
                data-cy="editorial-offering-hero-tagline"
                sx={{
                  display: 'block',
                  fontStyle: 'italic',
                  color: TEAL,
                  marginTop: '8px',
                }}
              >
                {tagline || headlineTagline}
              </Box>
            )}
          </Box>

          {description && (
            <>
              <Box
                ref={pitchRef}
                component="p"
                data-cy="editorial-offering-hero-description"
                sx={{
                  fontFamily: se3FontFamily.sans,
                  fontSize: '15px',
                  lineHeight: 1.7,
                  color: se3Colors.ink2,
                  margin: '24px 0 0',
                  maxWidth: HERO_COPY_MAX_WIDTH,
                  ...(pitchExpanded
                    ? {}
                    : {
                        display: '-webkit-box',
                        WebkitLineClamp: 4,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }),
                }}
              >
                {description}
              </Box>
              {pitchCanExpand && (
                <Box
                  component="button"
                  type="button"
                  data-cy="editorial-offering-hero-pitch-toggle"
                  onClick={() => setPitchExpanded((open) => !open)}
                  sx={{
                    marginTop: '10px',
                    display: 'block',
                    padding: 0,
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontFamily: se3FontFamily.sans,
                    fontSize: '13px',
                    fontWeight: 600,
                    letterSpacing: '0.04em',
                    color: TEAL,
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  {pitchExpanded ? 'Show less' : 'Show more'}
                </Box>
              )}
            </>
          )}

          {isClosed ? (
            <Box
              data-cy="editorial-offering-hero-closed"
              sx={{
                marginTop: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: '240px',
                width: 'fit-content',
                padding: '18px 40px',
                borderRadius: '12px',
                backgroundColor: se3Colors.gray2,
                color: se3Colors.onDark,
                fontSize: '15px',
                fontWeight: 600,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
              }}
            >
              Offering Closed
            </Box>
          ) : (
            <Box
              sx={{
                marginTop: '32px',
                // Waitlist uses the orange status treatment, matching the
                // sidebar; normal investing retains the editorial deep teal.
                // PD (Paige QA): scale the CTA up to match control/design — it read
                // too small at the default md size.
                '& a, & button': {
                  backgroundColor: `${isWaitlisted || isTTW ? colors.SEOrange : TEAL} !important`,
                  borderColor: `${isWaitlisted || isTTW ? colors.SEOrange : TEAL} !important`,
                  color: `${se3Colors.onDark} !important`,
                  minWidth: '240px',
                  padding: '18px 40px',
                  fontSize: '15px',
                  letterSpacing: '0.14em',
                },
              }}
            >
              <EditorialButton
                href={investUrl}
                onClick={handleCtaClick}
                variant="solid"
                size="md"
                data-cy="editorial-offering-hero-cta"
              >
                {ctaText}
              </EditorialButton>
            </Box>
          )}

          <Box
            component="p"
            data-cy="editorial-offering-hero-disclaimer"
            sx={{
              fontFamily: se3FontFamily.sans,
              fontSize: '11px',
              lineHeight: 1.5,
              color: se3Colors.ink3,
              margin: '28px 0 0',
              maxWidth: '52ch',
            }}
          >
            <OfferingLegalText offeringData={offeringData} />
          </Box>
        </Box>

        {/* Right column */}
        <Box sx={{ minWidth: 0 }}>
          {heroImage?.src || backgroundVideoUrl ? (
            <Box
              data-cy="editorial-offering-hero-image-card"
              sx={{
                position: 'relative',
                borderRadius: '18px',
                overflow: 'hidden',
                backgroundColor: se3Colors.night,
                color: se3Colors.onDark,
                // PD-36055 (Hannah): lock the hero to a fixed 16:9 frame so it
                // never reshapes on resize. The old `minHeight: 260px` floored
                // the height at narrow widths, overriding the ratio and changing
                // the frame's shape; dropping it keeps the frame exactly 16:9 at
                // every width. API-provided offering covers fill the frame,
                // while explicitly authored partner cards default to contain.
                aspectRatio: '16 / 9',
              }}
            >
              {!autoplayBackgroundVideo && heroImage?.src && (
                <Box
                  component="img"
                  src={heroImage.src}
                  alt={heroImage.alt || `${name} offering image`}
                  loading="eager"
                  fetchPriority="high"
                  decoding="async"
                  data-cy="editorial-offering-hero-image"
                  style={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: heroImageObjectFit,
                    objectPosition: 'center',
                    transform: `scale(${heroImageScale})`,
                    display: 'block',
                  }}
                />
              )}
              {videoUrl && !isVideoOpen && (
                <Box
                  component="button"
                  type="button"
                  data-cy="editorial-offering-hero-play"
                  aria-label="Play video"
                  onClick={() => setVideoOpen(true)}
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'grid',
                    placeItems: 'center',
                    border: 'none',
                    cursor: 'pointer',
                    background: 'rgba(6,24,25,0.28)',
                    transition: 'background 0.15s',
                    '&:hover': { background: 'rgba(6,24,25,0.4)' },
                  }}
                >
                  <Box
                    sx={{
                      width: '64px',
                      height: '64px',
                      borderRadius: '50%',
                      background: 'rgba(255,255,255,0.94)',
                      display: 'grid',
                      placeItems: 'center',
                      boxShadow: '0 6px 20px rgba(0,0,0,0.25)',
                    }}
                  >
                    <Box
                      component="svg"
                      viewBox="0 0 24 24"
                      sx={{ width: '26px', height: '26px', marginLeft: '3px', fill: TEAL }}
                    >
                      <path d="M8 5v14l11-7z" />
                    </Box>
                  </Box>
                </Box>
              )}
              {videoUrl && isVideoOpen && videoEmbedSrc && (
                <Box
                  component="iframe"
                  ref={autoplayBackgroundVideo ? backgroundVideoIframeRef : undefined}
                  src={videoEmbedSrc}
                  title={`${name} video`}
                  allow="autoplay; encrypted-media; fullscreen"
                  allowFullScreen
                  data-cy="editorial-offering-hero-video"
                  sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
                />
              )}
              {autoplayBackgroundVideo && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    pointerEvents: 'none',
                  }}
                >
                  <Box
                    component="button"
                    type="button"
                    data-cy={
                      backgroundVideoUnmuted
                        ? 'editorial-offering-hero-playpause'
                        : 'editorial-offering-hero-unmute'
                    }
                    aria-label={backgroundVideoControlLabel}
                    onClick={
                      backgroundVideoUnmuted
                        ? handleBackgroundVideoPlayPause
                        : handleBackgroundVideoUnmute
                    }
                    sx={{
                      position: 'absolute',
                      ...(backgroundVideoUnmuted
                        ? { right: 16, bottom: 16 }
                        : { left: '50%', top: '50%', transform: 'translate(-50%, -50%)' }),
                      width: backgroundVideoUnmuted ? '48px' : '64px',
                      height: backgroundVideoUnmuted ? '48px' : '64px',
                      borderRadius: '50%',
                      border: 'none',
                      background: backgroundVideoUnmuted
                        ? 'rgba(0,0,0,0.6)'
                        : 'rgba(255,255,255,0.94)',
                      color: backgroundVideoUnmuted ? '#FFFFFF' : TEAL,
                      display: 'grid',
                      placeItems: 'center',
                      boxShadow: '0 6px 20px rgba(0,0,0,0.25)',
                      cursor: 'pointer',
                      pointerEvents: 'auto',
                      '&:focus-visible': {
                        outline: '3px solid #FFFFFF',
                        outlineOffset: '2px',
                      },
                    }}
                  >
                    {backgroundVideoControlIcon}
                  </Box>
                </Box>
              )}
              {videoUrl && isVideoOpen && !videoEmbedSrc && (
                <Box
                  component="video"
                  src={videoUrl}
                  controls
                  autoPlay
                  data-cy="editorial-offering-hero-video"
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    background: '#000',
                  }}
                />
              )}
              {hasHeroImageOverlayContent && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    background:
                      'linear-gradient(180deg, rgba(6,24,25,0) 0%, rgba(6,24,25,0.82) 100%)',
                    pointerEvents: 'none',
                  }}
                />
              )}
              {hasHeroImageOverlayContent && (
                <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: 0, padding: '28px' }}>
                  {heroImage.headline && (
                    <Box
                      component="p"
                      data-cy="editorial-offering-hero-image-headline"
                      sx={{
                        fontFamily: se3FontFamily.serif,
                        fontSize: '24px',
                        lineHeight: 1.2,
                        margin: 0,
                        color: se3Colors.onDark,
                      }}
                    >
                      {heroImage.headline}
                    </Box>
                  )}
                  {heroImage.footnote && (
                    <Box
                      component="p"
                      sx={{
                        fontSize: '11px',
                        color: se3Colors.onDark3,
                        margin: '12px 0 0',
                        lineHeight: 1.5,
                      }}
                    >
                      {heroImage.footnote}
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          ) : (
            // PD-36055: no cover image → clean navy logo tile (matches the
            // design) instead of a gradient with overlapping headline text.
            <Box
              data-cy="editorial-offering-hero-logo-tile"
              sx={{
                borderRadius: '18px',
                overflow: 'hidden',
                backgroundColor: se3Colors.night,
                color: se3Colors.onDark,
                minHeight: '260px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'flex-start',
                padding: '32px',
              }}
            >
              <Box
                sx={{
                  fontFamily: se3FontFamily.serif,
                  fontWeight: 500,
                  fontSize: '30px',
                  lineHeight: 1.1,
                  color: se3Colors.onDark,
                }}
              >
                {name}
              </Box>
              {seriesLegalName && (
                <Box
                  sx={{
                    fontFamily: se3FontFamily.sans,
                    fontSize: '12px',
                    lineHeight: 1.5,
                    color: se3Colors.onDark3,
                    marginTop: '12px',
                    maxWidth: '40ch',
                  }}
                >
                  {seriesLegalName}
                </Box>
              )}
            </Box>
          )}

          {partnerCard && (
            <Box
              data-cy="editorial-offering-hero-partner-card"
              sx={{
                marginTop: heroImage ? '16px' : 0,
                border: `1px solid ${se3Colors.edge}`,
                borderRadius: '14px',
                backgroundColor: se3Colors.card,
                padding: '20px 24px',
              }}
            >
              {partnerCard.label && (
                <Box
                  sx={{
                    fontSize: '11px',
                    fontWeight: 600,
                    letterSpacing: '0.16em',
                    textTransform: 'uppercase',
                    color: ACCENT_GOLD,
                    marginBottom: '12px',
                  }}
                >
                  {partnerCard.label}
                </Box>
              )}
              {Array.isArray(partnerCard.logos) && partnerCard.logos.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '20px', alignItems: 'center' }}>
                  {partnerCard.logos.map((logo) => (
                    <Box
                      key={logo.src || logo.alt}
                      component="img"
                      src={logo.src}
                      alt={logo.alt || ''}
                      sx={{ height: '28px', width: 'auto', objectFit: 'contain' }}
                    />
                  ))}
                </Box>
              )}
              {partnerCard.footnote && (
                <Box
                  component="p"
                  sx={{
                    fontSize: '11px',
                    color: se3Colors.ink3,
                    margin: '14px 0 0',
                    lineHeight: 1.5,
                  }}
                >
                  {partnerCard.footnote}
                </Box>
              )}
            </Box>
          )}

          {showRaised && (
            <Box
              data-cy="editorial-offering-hero-raised-card"
              sx={{
                // Design (AtomBeam mock): white card, gold TOP border + soft
                // drop shadow, label left / amount right on one row.
                marginTop: heroImage || partnerCard ? '14px' : 0,
                border: `1px solid ${se3Colors.edge}`,
                borderTop: `2px solid ${ACCENT_GOLD}`,
                borderRadius: '14px',
                boxShadow: '0 10px 26px rgba(11,41,45,0.06)',
                backgroundColor: se3Colors.card,
                padding: '18px 24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '16px',
              }}
            >
              <Box
                data-cy="editorial-offering-hero-raised-label"
                sx={{
                  fontFamily:
                    '"IBM Plex Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
                  fontSize: '16px',
                  fontWeight: 400,
                  lineHeight: '20.8px',
                  letterSpacing: '0.125rem',
                  textTransform: 'uppercase',
                  color: ACCENT_GOLD,
                }}
              >
                {isTTW ? 'RESERVED' : 'RAISED'}
              </Box>
              <Box
                data-cy="editorial-offering-hero-raised-amount"
                sx={{
                  fontFamily: se3FontFamily.serif,
                  // Match the invest sidebar's stat values (serif 500) so the hero
                  // "Raised" figure and the sidebar "Min Invest" read consistently
                  // (QA: raised looked different from min invest).
                  fontWeight: 500,
                  fontSize: '26px',
                  lineHeight: 1,
                  color: TEAL,
                }}
              >
                {formatCurrency.format(amountRaised)}
              </Box>
            </Box>
          )}
        </Box>
      </Box>
      {renderLoginModal({
        offering: offeringData,
        verifiedPrimaryUserId,
        onClose: (loginUser) => {
          if (loginUser?.event === 'signup') {
            navigateToInvestUrl(redirectUrl);
          }
        },
        onSuccessfulLogin: (response) => {
          if (response?.error) return;
          navigateToInvestUrl(redirectUrl);
        },
      })}
    </Box>
  );
};

EditorialOfferingHero.propTypes = {
  offeringData: PropTypes.shape({
    name: PropTypes.string,
    slug: PropTypes.string,
    regulationType: PropTypes.string,
    targetAudienceAccreditation: PropTypes.string,
    portfolioCompanyName: PropTypes.string,
    portfolio_company_name: PropTypes.string,
    underlyingCompanyName: PropTypes.string,
    underlying_company_name: PropTypes.string,
    companyName: PropTypes.string,
    company_name: PropTypes.string,
    issuerName: PropTypes.string,
    series: PropTypes.string,
    seriesName: PropTypes.string,
    series_name: PropTypes.string,
    entityName: PropTypes.string,
    entity_name: PropTypes.string,
    company: PropTypes.shape({
      dbaName: PropTypes.string,
      name: PropTypes.string,
      legalName: PropTypes.string,
    }),
    entity: PropTypes.shape({
      name: PropTypes.string,
      legalName: PropTypes.string,
    }),
    startengineCompany: PropTypes.shape({
      companyName: PropTypes.string,
    }),
    elevatorPitch: PropTypes.string,
    summary: PropTypes.string,
    coverDesktopUrl: PropTypes.string,
    coverTombstoneUrl: PropTypes.string,
    videoUrl: PropTypes.string,
    amountRaised: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    minimumInvestmentAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    valuationCap: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    pricePerShare: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  /** Optional bespoke italic serif tagline line (Builder-authored content). */
  tagline: PropTypes.node,
  /** Optional category label prefix for the eyebrow (e.g. "DATA COMPACTION"). */
  eyebrowCategory: PropTypes.string,
  /** Optional dark hero image card (Builder-authored). */
  heroImage: PropTypes.shape({
    src: PropTypes.string.isRequired,
    alt: PropTypes.string,
    objectFit: PropTypes.oneOf(['contain', 'cover']),
    headline: PropTypes.node,
    footnote: PropTypes.node,
  }),
  /** Optional partner-logo card (Builder-authored). */
  partnerCard: PropTypes.shape({
    label: PropTypes.node,
    logos: PropTypes.arrayOf(PropTypes.shape({ src: PropTypes.string, alt: PropTypes.string })),
    footnote: PropTypes.node,
  }),
  /** Show the "MOST MOMENTUM" pill (only when the data/CMS supports it). */
  showMomentumPill: PropTypes.bool,
  /** Current amountRaised endpoint payload. Prefer this over initial offering props. */
  amountRaisedData: PropTypes.shape({
    amountRaised: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    totalAmountRaised: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  /** Builder-authored Vimeo URL that should autoplay muted and loop as a background. */
  backgroundVideoUrl: PropTypes.string,
};

export default EditorialOfferingHero;
